import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, X } from "@fg/icons";
import { useMeetings } from "@/api/hooks";
import { isBreakoutSubject } from "@/lib/breakout";
import { notifyChat } from "@/lib/chatNotify";

/** How long before the start the reminder appears. */
const LEAD_MS = 5 * 60_000;
const SEEN_KEY = "fg.meeting.remindedIds";

function readSeen(): string[] {
    try { return JSON.parse(window.localStorage.getItem(SEEN_KEY) ?? "[]") as string[]; } catch { return []; }
}
function remember(id: string) {
    try { window.localStorage.setItem(SEEN_KEY, JSON.stringify([...readSeen().filter((x) => x !== id), id].slice(-100))); } catch { /* shown again next time */ }
}

/**
 * Teams' meeting reminder: about five minutes before a meeting you host or were invited to starts, a card appears with a Join
 * button (and the usual chime / banner from Settings). Calls, cancelled meetings, breakout rooms and meetings you already
 * reminded yourself about are skipped; each meeting is reminded once per browser.
 */
export function MeetingReminders() {
    const meetings = useMeetings();
    const navigate = useNavigate();
    const [due, setDue] = useState<{ id: string; subject: string; start: Date } | null>(null);
    const data = useRef(meetings.data);
    data.current = meetings.data;

    const check = useCallback(() => {
        const now = Date.now();
        const seen = new Set(readSeen());
        const next = (data.current ?? [])
            .filter((m) => !m.isCall && (m.status === "Scheduled" || m.status === "Live") && !isBreakoutSubject(m.subject) && !seen.has(m.id))
            .map((m) => ({ m, at: new Date(m.scheduledStart).getTime() }))
            // From five minutes before until ten minutes after the start (someone who opens the app late still gets it).
            .filter(({ at }) => at - now <= LEAD_MS && now - at <= 10 * 60_000)
            .sort((a, b) => a.at - b.at)[0];
        if (!next) return;
        remember(next.m.id);
        setDue({ id: next.m.id, subject: next.m.subject, start: new Date(next.at) });
        notifyChat({ title: next.m.subject, body: "Your meeting is about to start.", showOsNotification: true, loud: document.visibilityState !== "visible" });
    }, []);

    // Check when the meeting list arrives or changes, then every half minute.
    useEffect(() => { check(); }, [check, meetings.data]);
    useEffect(() => {
        const t = window.setInterval(check, 30_000);
        return () => window.clearInterval(t);
    }, [check]);

    if (!due) return null;
    const mins = Math.round((due.start.getTime() - Date.now()) / 60_000);
    return (
        <div role="alert" className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border bg-popover p-4 text-popover-foreground shadow-2xl">
            <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white" aria-hidden><CalendarClock className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{due.subject}</p>
                    <p className="text-xs text-muted-foreground">
                        {mins > 0 ? `Starts in ${mins} minute${mins === 1 ? "" : "s"}` : mins === 0 ? "Starting now" : `Started ${-mins} minute${mins === -1 ? "" : "s"} ago`}
                        {" · "}{due.start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </p>
                </div>
                <button type="button" onClick={() => setDue(null)} aria-label="Dismiss reminder" className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => setDue(null)} className="rounded-md px-3 py-1.5 text-xs hover:bg-accent">Dismiss</button>
                <button type="button" onClick={() => { setDue(null); navigate(`/meeting/${due.id}`); }} className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">Join</button>
            </div>
        </div>
    );
}
